import { useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Home, Users, Package, Box, ClipboardList, TrendingUp, ShoppingBag, BadgeEuro, Scale, Truck, Download, Mail, Boxes, ShieldCheck, Link2, Building2, BriefcaseBusiness, KeyRound } from "lucide-react";

import useMediaQuery from "../../components/tools/useMediaQuery.jsx";
import { useEditor } from "../../App.jsx";
import { useAuthContext } from "../../components/providers/AuthProvider.jsx";
import { ProyectoEditorWrapper } from "../Profile/Profile2.jsx";
import AdminRoutes from "./AdminRoutes.jsx";
import Menu2Simple from "./Menu2.jsx";
import { useMenu } from "./MenuContext.jsx"; // Importar el hook del contexto
import { canManageProjects } from "../../lib/permissions.js";

export default function Admin2() {
    const authContext = useAuthContext();
    const { stateEditor } = useEditor();
    const isMobile = useMediaQuery("(max-width: 1024px)");

    // Usar el estado del contexto en lugar del estado local
    const { mobileMenuOpen, toggleMenu, closeMenu } = useMenu();
    const mobileMenuRef = useRef(null);

    // `role === "admin"` es el acceso grueso al panel; algunas secciones
    // exigen además un permiso Django explícito (p. ej. envíos toca precios y
    // el checkout físico real). Sin él, el enlace ni siquiera se muestra — el
    // backend vuelve a exigir el permiso en cada endpoint, así que ocultar el
    // enlace es una ayuda de navegación, no la defensa real.
    const canManageShipping      = Boolean(authContext.permissions?.includes("store.manage_shipping_config"));
    const canManageEntitlements  = Boolean(authContext.permissions?.includes("CustomizedKeyring.manage_digital_entitlement"));
    const canManageProduction    = Boolean(authContext.permissions?.includes("fulfillment.manage_filament_references"));

    const canManageCommunications = Boolean(authContext.permissions?.includes("communications.manage_communication"));
    // C3D-38: el rol comercial entra solo al CRM (proyectos, leads, clientes).
    const canProjects = canManageProjects(authContext);

    // Agrupado por para qué se entra. Los bloques que casi no se tocan una vez
    // configurados (operación, CRM, ajustes) arrancan plegados; el que tiene la
    // sección abierta se despliega solo.
    const navGroups = useMemo(() => {
        const groups = [{ id: "inicio", items: [{ to: "general", icon: Home, label: "General" }] }];
        const crmItems = [
            { to: "proyectos", icon: BriefcaseBusiness, label: "Proyectos" },
            { to: "leads", icon: TrendingUp, label: "Leads" },
            { to: "clientes", icon: Building2, label: "Clientes" },
            // C3D-41: tokens para el MCP de prospección.
            { to: "tokens_api", icon: KeyRound, label: "Tokens de API" },
        ];
        if (authContext.role !== "admin") {
            return canProjects ? [{ id: "crm", label: "CRM", items: crmItems }] : groups;
        }
        return [
            ...groups,
            { id: "ventas", label: "Ventas", items: [
                { to: "pedidos", icon: ShoppingBag, label: "Pedidos" },
                { to: "solicitudes", icon: ClipboardList, label: "Solicitudes" },
            ] },
            { id: "catalogo", label: "Catálogo", items: [
                { to: "productos", icon: Package, label: "Productos" },
                { to: "producto_digital", icon: BadgeEuro, label: "Producto digital" },
                ...(canManageEntitlements ? [{ to: "entitlements", icon: Download, label: "Licencias digitales" }] : []),
            ] },
            { id: "disenos", label: "Diseños", items: [
                { to: "todo_modelos_creados", icon: Box, label: "Modelos todos" },
                { to: "enlaces_publicos", icon: Link2, label: "Enlaces públicos" },
            ] },
            { id: "personas", label: "Personas", items: [
                { to: "usuarios", icon: Users, label: "Usuarios" },
                { to: "roles", icon: ShieldCheck, label: "Roles y permisos" },
            ] },
            { id: "operacion", label: "Operación", collapsed: true, items: [
                ...(canManageProduction ? [{ to: "produccion", icon: Boxes, label: "Producción" }] : []),
                ...(canManageShipping ? [{ to: "envios", icon: Truck, label: "Envíos" }] : []),
                ...(canManageCommunications ? [{ to: "communications", icon: Mail, label: "Comunicaciones" }] : []),
            ] },
            { id: "crm", label: "CRM", collapsed: true, items: crmItems },
            { id: "ajustes", label: "Ajustes", collapsed: true, items: [
                { to: "legal", icon: Scale, label: "Legal" },
            ] },
        ].filter((group) => group.items.length);
    }, [authContext.role, canManageShipping, canManageEntitlements, canManageProduction, canManageCommunications, canProjects]);

    const handleMenuToggle = () => {
        toggleMenu();
    };

    const handleMenuItemClick = () => {
        closeMenu();
    };

    const location = useLocation();
    console.log("Admin component rendered with location:", location.pathname);

    return (
        <div className="w-full h-full min-h-full max-h-full p-3 flex flex-col lg:flex-row gap-3">
            <Menu2Simple
                stateEditor={stateEditor}
                isMobile={isMobile}
                mobileMenuOpen={mobileMenuOpen}
                handleMenuToggle={handleMenuToggle}
                handleMenuItemClick={handleMenuItemClick}
                mobileMenuRef={mobileMenuRef}
                navGroups={navGroups}
            />

            <main className="w-full lg:w-10/12 flex-1 overflow-y-auto overflow-x-hidden">
                {stateEditor?.editor ? (
                    <ProyectoEditorWrapper
                        setEditor={() => {}}
                        editorType={null}
                        id={null}
                        setEditorID={() => {}}
                        setEditorType={() => {}}
                    />
                ) : (
                    <AdminRoutes />
                )}
            </main>
        </div>
    );
}
